import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, PackageCheck } from "lucide-react";
import type { PlanConversionPreview } from "@wedding/shared";
import { adminUsersApi } from "../lib/api";
import { cn } from "../lib/utils";

type ConvertResult = { from: string; to: string; orders: Array<{ number: string; weddingSlug: string }> };

/**
 * Kullanıcı detayı → "Hesap planı → düğün paketleri" (2026-10-15 kararı: "Admin aracıyla dönüştür").
 * "Paket = tek düğün" modelinden önce Pro/Max HESAP planı atanmış organizatör: seçilen her düğüne
 * 0 ₺ geçiş siparişi (G-önekli no, defterde), hesap varsayılan plana iner. Test düğünleri seçilmez.
 * Free hesapta görünmez; özel (kurumsal) planda yalnız "dönüştürülmez" notu.
 */
export function PlanConversionCard({
  organizerId,
  onConverted,
  showToast,
}: {
  organizerId: string;
  onConverted: (plan: string) => void;
  showToast: (msg: string, ok?: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const [preview, setPreview] = useState<PlanConversionPreview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await adminUsersApi.get<PlanConversionPreview>(`/${organizerId}/conversion`);
      if (!res.data) return;
      setPreview(res.data);
      // Varsayılan: sahibi olduğu, paketsiz, AKTİF düğünler (arşiv/test elle seçilir).
      setSelected(new Set(res.data.weddings.filter((w) => w.owner && !w.order && w.status === "active").map((w) => w.id)));
    } catch (err) {
      showToast(t("planChange.conversion.loadFailed", { error: err instanceof Error ? err.message : String(err) }), false);
    }
  }, [organizerId, showToast, t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!preview || preview.plan.slug === preview.defaultPlan) return null;

  if (!preview.convertible) {
    return (
      <div className="px-5 py-4 border-t border-border/60" data-testid="plan-conversion">
        <p className="text-xs text-muted-foreground">{t("planChange.conversion.notConvertible", { plan: preview.plan.label })}</p>
      </div>
    );
  }

  const eligible = preview.weddings.filter((w) => w.owner && !w.order);
  const unselected = eligible.filter((w) => !selected.has(w.id)).length;
  const toggle = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const convert = async () => {
    const vars = { count: selected.size, plan: preview.plan.label, to: preview.defaultPlan };
    if (!window.confirm(t("planChange.conversion.confirm", vars))) return;
    setSaving(true);
    try {
      const res = await adminUsersApi.post<ConvertResult>(`/${organizerId}/convert`, {
        weddingIds: [...selected],
        expectedPlan: preview.plan.slug,
      });
      const orders = res.data?.orders ?? [];
      showToast(
        orders.length
          ? t("planChange.conversion.done", { count: orders.length, numbers: orders.map((o) => o.number).join(", ") })
          : t("planChange.conversion.doneNone", { to: preview.defaultPlan }),
      );
      // Plan değişti → kart `key` ile yeniden kurulur ve önizlemeyi tazeler.
      onConverted(res.data?.to ?? preview.defaultPlan);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      showToast(msg === "plan_changed" ? t("planChange.conversion.planChanged") : t("planChange.conversion.failed", { error: msg }), false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-5 py-4 border-t border-border/60 space-y-3" data-testid="plan-conversion">
      <div>
        <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <PackageCheck className="h-4 w-4 text-primary" aria-hidden="true" />
          <span>{t("planChange.conversion.title")}</span>
        </p>
        <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
          {t("planChange.conversion.desc", { plan: preview.plan.label, to: preview.defaultPlan })}
        </p>
      </div>

      <ul className="divide-y divide-border rounded-xl border border-border">
        {preview.weddings.map((w) => {
          const disabled = !w.owner || !!w.order;
          return (
            <li key={w.id}>
              <label
                className={cn(
                  "flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2.5 text-sm",
                  disabled ? "opacity-60" : "cursor-pointer hover:bg-primary/5",
                )}
              >
                <input
                  type="checkbox"
                  checked={selected.has(w.id)}
                  disabled={disabled || saving}
                  onChange={() => toggle(w.id)}
                  className="h-4 w-4 accent-primary cursor-pointer disabled:cursor-not-allowed"
                />
                <span className="font-medium">{w.title}</span>
                <span className="font-mono text-[11px] text-muted-foreground">/{w.slug}</span>
                {w.status === "archived" && <span className="text-[11px] text-muted-foreground">({t("planChange.conversion.archived")})</span>}
                <span className="ml-auto flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
                  {w.date_time && <span>{new Date(w.date_time).toLocaleDateString(i18n.language)}</span>}
                  <span>{t("planChange.conversion.guests", { count: w.guests })}</span>
                  {w.order ? (
                    <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-emerald-700 dark:text-emerald-400">
                      {t("planChange.conversion.hasOrder", { number: w.order.number })}
                    </span>
                  ) : !w.owner ? (
                    <span>{t("planChange.conversion.notOwner")}</span>
                  ) : null}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {unselected > 0 && (
        <p
          data-testid="plan-conversion-warning"
          className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-800 dark:text-amber-300"
        >
          {t("planChange.conversion.unselectedWarn", { count: unselected, guests: preview.trialMaxGuests })}
        </p>
      )}

      <button
        type="button"
        onClick={() => void convert()}
        disabled={saving}
        className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
      >
        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />}
        <span>{t("planChange.conversion.button")}</span>
      </button>
    </div>
  );
}
